import { DialogContent } from '@repo/ui/components/dialog';
import type { ReactNode } from 'react';

import lockupUrl from '@/assets/brand/garden-lockup.png';
import lockupReversedUrl from '@/assets/brand/garden-lockup-reversed.png';

/**
 * The signage board every signed-out screen is set into.
 *
 * **The datum is the signature.** Two heavy rules span the full viewport, and
 * everything the screen has to say is placed relative to them: above the upper
 * datum is who and where, below the lower one is what this is and who may use
 * it, and the work happens in the measured field between. It is lifted from the
 * ruled line on an institutional garden plate — the enamelled bed markers and
 * survey boards a working horticultural estate is covered in.
 *
 * It also fixes the failure that killed three earlier attempts at this screen.
 * Each put a small block in the middle of a very large empty page; the rules
 * give the page a structure that reaches its own edges, so the form is set into
 * something instead of floating in leftover space.
 *
 * The header carries the lockup alone. It was a mark and the word "Munnar" set
 * beside it, until the real asset arrived with "Munnar" already in the wordmark;
 * keeping both said it twice. The bar is sized from its content rather than to a
 * fixed height, so a re-cut mark pushes the datum down instead of overlapping it.
 *
 * ## The register inside the datum — revised 25 August 2026
 *
 * The datum fixed the frame and left the middle unsolved. At 1920 what sat
 * between the rules was a 380px column of form in a 1920px page: about 770px of
 * dead ground each side and 950px of empty height under it. The page executed
 * the chrome of the idea — a rule, tracked caps — and none of its substance, and
 * it never showed a plant.
 *
 * Two changes, and both are borrowed rather than invented.
 *
 * **The rail and the field**, exactly the register that
 * `routes/back-office/Screen.tsx` already runs on every signed-in screen: the
 * same `84rem` extent, the same `16rem` rail, the same `gap-x-10`. The rail
 * carries the title, the sentence explaining why somebody is here, and the
 * standing facts; the form sits at the field's left edge, where records start on
 * every other screen in the app. The rail is an **alignment edge, not a drawn
 * line** — `Screen.tsx` has no `border-r` and neither does this. Below `lg` it
 * stacks and the screen is the single column it always was.
 *
 * **A Neelakurinji specimen**, drawn in line and bleeding off the right and
 * bottom. See `Specimen` below for why it is that plant and not a generic sprig.
 *
 * **Once it stacks, the form comes before the facts.** Side by side the rail's
 * order is invisible — the eye takes the form directly, and the facts are simply
 * beside it. In one column they are three paragraphs of reference material
 * between somebody and the staff-code field they came here to type in. The form
 * is the task; the facts are what to do when the task fails. The title and the
 * arrival line stay first in both, because they say what this screen is and why
 * the person was sent to it, which is the one thing worth reading before the
 * field. The DOM order is the stacked order and `lg` re-places all three, so the
 * form is early in the tab order at every width.
 *
 * Neither spends a new colour, a new face or a second signature device. The
 * boldness is still on the datum.
 */

/** Signage lettering: the display face, in caps, tracked open like plate text. */
const PLATE = 'font-display text-xs font-semibold tracking-[0.16em] uppercase';

/**
 * The id of the arrival line in the rail.
 *
 * Exported because the screen inside the frame is what knows which field takes
 * focus on mount, and that field has to name this line as its description — the
 * sentence explaining why somebody is looking at this page is otherwise never
 * spoken to the person who was thrown out mid-task. The frame owns the element
 * and its lettering; the screen owns the wiring.
 */
export const authDescriptionId = 'auth-arrival-note';

export function AuthFrame({
  title,
  description,
  facts,
  children,
}: {
  /** The screen's name, in the rail. */
  title: string;
  /** One line under it: why this person is looking at this screen. */
  description: ReactNode;
  /** The standing facts, below the rail's rule. `AuthFrame.Fact` each. */
  facts: ReactNode;
  /** The form, at the field's left edge. */
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="flex items-center gap-4 px-6 py-5 sm:px-10">
        <AuthFrame.Mark />
      </header>

      {/* The datum. 3px and in the ink colour, not a hairline in the border
          colour — a hairline here would read as newsprint rather than as a
          painted line, and the whole composition rests on it being emphatic. */}
      <div className="h-[3px] w-full shrink-0 datum bg-foreground" />

      <main className="relative flex flex-1 flex-col px-6 py-14 sm:px-10">
        {/* The specimen sits in its own layer behind the register, clipped to
            the field between the datums so it bleeds off the right and the
            bottom rather than ending in mid-air. It is decorative and takes no
            pointer: nothing here is a control and there is nothing to read. */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <Specimen />
        </div>

        {/* `my-auto` and not `justify-center`: identical until content outgrows
            the window, and then centred flex content overflows past the top edge
            with nothing to scroll back to.

            `relative` puts the register above the specimen without a z-index —
            a positioned element beats a static one at the same stacking level,
            and the drawing is a static child of a positioned layer. */}
        <div className="relative mx-auto my-auto grid w-full max-w-[84rem] rise grid-cols-1 gap-x-10 lg:grid-cols-[16rem_minmax(0,1fr)] lg:grid-rows-[auto_1fr]">
          {/* Three items and not two, so the stack can put the form before the
              facts while the rail still reads top to bottom. See the note above
              the component: the DOM order is the stacked order, and `lg`
              re-places all three explicitly.

              The rail has no `border-r`: it is the edge everything measures
              from, and drawing it would add a second rule to a page whose
              signature is already a rule. */}
          <div className="lg:col-start-1 lg:row-start-1">
            <h1 className="font-heading text-2xl font-medium tracking-tight text-foreground">
              {title}
            </h1>

            <p id={authDescriptionId} className="mt-4 text-sm text-muted-foreground">
              {description}
            </p>
          </div>

          {/* The field. `max-w-sm` and left-anchored: a form does not widen with
              the viewport, and where it starts is where the records start on
              every signed-in screen.

              `lg:row-span-2` is what keeps the wide composition identical. The
              form is usually the tallest thing here, and a grid item confined to
              row 1 would make row 1 as tall as the form — stranding the facts
              level with its bottom edge instead of under the description.
              Spanning both rows hands the sizing back to the rail: row 1 is the
              title and the arrival line, row 2 is `1fr` and swallows whatever
              slack the form leaves, so the facts stay put. */}
          <div className="mt-10 w-full max-w-sm lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:mt-0">
            {children}
          </div>

          {/* Hairline and not a filled block, the same way the back office
              separates a rail's facts from its description. Depth here is
              tonal; the rail is the page's own ground, not a third surface.

              **The row gap lives on the items, not on the grid**, and that is
              what keeps the stacked page the height it already was. Three bands
              with a `gap-y-10` between each would be 8px taller than the two
              bands this replaced, which is enough to push the lower datum off a
              390x844 window that used to hold it. Carried per item, the form
              takes the 40px band gap when it stacks and none at `lg`, and the
              facts keep the same 32px above their rule at both widths — the
              rail's own rhythm, unchanged, and no negative margin anywhere. A
              boundary already drawn by a rule does not also need a full band of
              air. */}
          <div className="mt-8 space-y-4 border-t border-border pt-6 lg:col-start-1 lg:row-start-2">
            {facts}
          </div>
        </div>
      </main>

      <div className="h-[3px] w-full shrink-0 datum bg-foreground" />

      <footer className="flex items-baseline justify-between gap-4 px-6 py-5 sm:px-10">
        <span className={`${PLATE} text-muted-foreground`}>Back office</span>
        <span className={`${PLATE} text-muted-foreground`}>Staff access only</span>
      </footer>
    </div>
  );
}

/**
 * A standing fact in the rail: signage label over a value.
 *
 * The same device as `ScreenFact` in the back office, and deliberately the same
 * device rather than a second one — caps, tracked, `font-display`, over a plain
 * line of body type. `docs/design-language.md` reserves that treatment for frame
 * lettering and short labels, which is exactly what these are.
 *
 * They exist because the page said nothing about what to do when you cannot get
 * in. A person standing at a terminal that will not take them had no sentence to
 * read; now the rail carries the three that answer it.
 */
AuthFrame.Fact = function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className={`${PLATE} text-muted-foreground`}>{label}</p>
      <p className="mt-1 text-sm text-foreground">{children}</p>
    </div>
  );
};

/**
 * *Strobilanthes kunthiana* — the Neelakurinji, drawn as a herbarium sheet
 * would draw it: outline, venation, a terminal flowering spike, and no colour.
 *
 * **Why this plant.** Three earlier attempts at this screen were rejected for
 * looking like nothing in particular, and a generic sprig would have been a
 * fourth. The Neelakurinji is Munnar's own: it flowers once every twelve years
 * and turns these hills blue, and it is the reason people come. It is specific
 * to this place rather than to "a garden", which is the whole of the point.
 *
 * **Why it is drawn this way.** Line only, in gauge — the hairline value, the
 * one this system already uses for a painted rule — with the stems in reed held
 * back so the structure sits behind the leaves. It spends **no colour**: the
 * page still has exactly one accent and it is on the button. A tinted flower
 * here would take the one thing the sign-in button has.
 *
 * **Why it bleeds.** At ~196% of the field's height and running off the right
 * and the bottom, the page reads as being *in* the garden rather than as showing
 * a picture of one in a box — and a contained illustration beside a form is the
 * template answer this brief has already refused once.
 *
 * It does not animate. The datum draws and the register rises; a third moving
 * thing would make an authored arrival into a sequence.
 *
 * The tubular bell with the five-lobed flared mouth is what makes this
 * *Strobilanthes* and not a daisy. Do not simplify the floret away.
 */
function Specimen() {
  return (
    <svg
      viewBox="0 0 760 1180"
      fill="none"
      aria-hidden="true"
      // Below `lg` it is pushed further off the edge, shortened and faded: on a
      // phone the register is the whole page and the specimen is a margin note.
      //
      // **The offset is a translate of its own width, and not a `vw` figure,
      // and that is the one thing here worth not undoing.** The drawing is sized
      // by height, so its width comes from the viewport's *height*; an offset in
      // `vw` is measured against the viewport's *width*. The two disagree the
      // moment the window is not the shape it was tuned on. FE064 was agreed on
      // `right: -14vw / -46vw`, and the small-screen half of that did not
      // survive a render: at 390x844 a 150%-tall specimen is ~690px wide against
      // a 390px page, `-46vw` is only 179px, and the stem ran straight down the
      // middle of the standing facts with the leaves crossing every line of
      // them. The obvious repair made it worse in the other direction — pushing
      // to `-64vw` cleared 390 and then threw all but two leaves off the screen
      // at 768x1024, where the same fraction is 491px. A percentage translate is
      // of the element's own box, so it holds at every shape.
      //
      // `22%` reproduces the agreed wide composition exactly — it is what
      // `-14vw` measured at both 1920x1080 and 1280x800, which is why those two
      // renders are unchanged. `42%` and the shorter `128%` are the small-screen
      // settlement: the stem sits past the right edge of the text column and
      // only the reaching branch crosses the page, through the gap between the
      // facts and the form. The opacity comes down with it, because at that size
      // the specimen is behind body copy rather than beside it.
      className="absolute top-[-18%] right-0 h-[128%] w-auto translate-x-[42%] text-border opacity-45 lg:top-[-26%] lg:h-[196%] lg:translate-x-[22%] lg:opacity-100"
    >
      <defs>
        <g id="leaf">
          <path
            d="M0 0 C 34 -32, 120 -38, 172 0 C 120 38, 34 32, 0 0 Z"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinejoin="round"
          />
          <path d="M0 0 C 60 -3, 120 -2, 172 0" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M28 -2 C 50 -16, 62 -21, 80 -24 M60 -2 C 82 -14, 96 -18, 114 -20
               M92 -2 C 112 -11, 124 -13, 140 -14
               M28 2 C 50 16, 62 21, 80 24 M60 2 C 82 14, 96 18, 114 20
               M92 2 C 112 11, 124 13, 140 14"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </g>
        {/* a tubular bell with a five-lobed flared mouth: the thing that
            makes this Strobilanthes and not a generic sprig */}
        <g id="floret">
          <path
            d="M0 0 C 8 -7, 18 -14, 27 -19 C 36 -24, 47 -22, 50 -15
               C 56 -18, 63 -13, 61 -6 C 67 -4, 67 4, 60 6
               C 61 13, 53 17, 47 13 C 42 20, 31 19, 27 12
               C 17 9, 7 5, 0 0 Z"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinejoin="round"
          />
          <path
            d="M50 -15 C 48 -9, 47 -3, 48 3 M61 -6 C 56 -3, 53 0, 52 4
               M47 13 C 46 8, 45 4, 46 0"
            stroke="currentColor"
            strokeWidth="1.15"
            strokeLinecap="round"
          />
          <path d="M4 1 C 12 0, 20 2, 26 5" stroke="currentColor" strokeWidth="1.15" />
        </g>
      </defs>

      {/* Main stem, entering bottom-right and leaning left as it climbs. In reed
          and held back, so the woody structure reads behind the foliage rather
          than as the heaviest thing on the page. */}
      <g className="text-muted-foreground opacity-[0.42]">
        <path
          d="M470 1180 C 452 1000, 430 840, 408 700 C 390 586, 374 470, 366 372"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <path
          d="M482 1180 C 464 1000, 442 840, 420 700 C 402 586, 386 470, 378 372"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* a branch reaching left across the middle, so the specimen holds
            the width instead of being a stick at the edge */}
        <path
          d="M418 742 C 356 706, 288 676, 214 660"
          stroke="currentColor"
          strokeWidth="2.9"
          strokeLinecap="round"
        />
      </g>

      {/* leaf pairs on the main stem */}
      <use href="#leaf" transform="translate(452,1060) rotate(-22) scale(1.62)" />
      <use href="#leaf" transform="translate(446,1048) rotate(198) scale(1.44)" />
      <use href="#leaf" transform="translate(424,858)  rotate(-28) scale(1.4)" />
      <use href="#leaf" transform="translate(418,846)  rotate(204) scale(1.24)" />
      <use href="#leaf" transform="translate(404,676)  rotate(-34) scale(1.16)" />
      <use href="#leaf" transform="translate(398,664)  rotate(210) scale(1.0)" />
      <use href="#leaf" transform="translate(382,518)  rotate(-40) scale(.9)" />
      <use href="#leaf" transform="translate(376,508)  rotate(216) scale(.76)" />

      {/* leaves on the reaching branch */}
      <use href="#leaf" transform="translate(330,690) rotate(160) scale(1.06)" />
      <use href="#leaf" transform="translate(300,660) rotate(-6) scale(.78)" />
      <use href="#leaf" transform="translate(228,668) rotate(168) scale(.86)" />

      {/* terminal flowering spike: the once-in-twelve-years bloom */}
      <path
        d="M366 372 C 366 320, 362 272, 364 228 C 365 200, 364 184, 363 170"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
      <use href="#floret" transform="translate(370,368) rotate(-40) scale(1.55)" />
      <use href="#floret" transform="translate(358,330) rotate(-150) scale(1.42)" />
      <use href="#floret" transform="translate(374,296) rotate(-28) scale(1.34)" />
      <use href="#floret" transform="translate(354,258) rotate(-158) scale(1.2)" />
      <use href="#floret" transform="translate(372,226) rotate(-44) scale(1.08)" />
      <use href="#floret" transform="translate(352,196) rotate(-146) scale(.92)" />
      <use href="#floret" transform="translate(366,172) rotate(-70) scale(.78)" />
      {/* unopened bracts at the very tip */}
      <path
        d="M363 170 C 355 154, 353 142, 356 130 M363 170 C 373 156, 377 144, 375 132
           M363 152 C 361 140, 361 132, 363 124"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * The identity slot — the one place the lockup goes, so every signed-out screen
 * picks it up at once.
 *
 * Two cuts, because the supplied lockup has a reversed variant and the app has
 * a dark theme. The positive has a dark green wordmark and dies on the dark
 * ground; the reversed has a white one and dies on mist. Swapping them in CSS
 * rather than in JS keeps it correct on the very first paint, before React has
 * run — which on a terminal cold-starting from `app://` is a visible moment.
 *
 * Height is fixed and width is `auto` so the aspect ratio comes from the file
 * rather than from a number here that would silently be wrong if the asset is
 * ever re-cut. The two files are different pixel sizes — 686x100 and 1372x200 —
 * and that is deliberate: the reversed one is the 2x asset their site ships.
 *
 * **It is not a link.** The obvious thing — click the logo, go home — is wrong
 * on a signed-out screen: there is nowhere to go, and offering a door that does
 * not open is worse than offering none.
 */
AuthFrame.Mark = function Mark() {
  return (
    <>
      <img
        src={lockupUrl}
        alt="Government Botanical Garden Munnar"
        className="h-6 w-auto dark:hidden"
      />
      <img src={lockupReversedUrl} alt="" aria-hidden className="hidden h-6 w-auto dark:block" />
    </>
  );
};

/**
 * The same identity with the wordmark cropped off — the emblem alone.
 *
 * **For the terminals only, and it exists because the kiosk says the name
 * twice.** The Electron window title already reads *Government Botanical Garden
 * Munnar* and the lockup repeated it about 60px below it. On a browser tab that
 * is chrome nobody reads as part of the page; on a full-screen till it is the
 * same string, twice, in the same eyeful. The mark still has to be there — a
 * terminal left on all night has to say what it is — so what goes is the words.
 *
 * A second slot rather than a variant on {@link AuthFrame.Mark}, because the
 * signed-out frame keeps its whole lockup and that is not being reopened. It is
 * still the case that **a lockup file is referenced here and nowhere else**,
 * which is the rule `docs/design-language.md` states at the mark.
 *
 * **The crop is arithmetic on the file rather than a second asset.** Measured
 * off the alpha channel of `garden-lockup.png` (686x100): the emblem occupies
 * columns 0-77 and the wordmark starts at 113. `object-cover` on a square box
 * scales the file by `max(w/686, h/100)` — the height, since it is far the
 * wider — so a square box shows exactly the leftmost 100 source columns, which
 * is the emblem and 23px of the gap after it, and stops 13px short of the "G".
 * A re-cut asset with different proportions is the one thing that would break
 * that, which is the same caveat the reversed cut already carries.
 *
 * The dark swap goes with it, for the reason `Mark` gives: in CSS so it is right
 * on the first paint, before React has run.
 */
AuthFrame.Emblem = function Emblem() {
  return (
    <>
      <img
        src={lockupUrl}
        alt="Government Botanical Garden Munnar"
        className="size-8 object-cover object-left dark:hidden"
      />
      <img
        src={lockupReversedUrl}
        alt=""
        aria-hidden
        className="hidden size-8 object-cover object-left dark:block"
      />
    </>
  );
};

/**
 * A field label, set as instrument lettering rather than as form chrome.
 *
 * Caps cost nothing in readability at one or two words, and they are what makes
 * this read as equipment a shift is run on rather than as a web form. Anything
 * longer than a short noun phrase belongs in body type, not here.
 */
export const fieldLabel = `${PLATE} text-muted-foreground`;

/**
 * A field somebody writes in: enamel, with the gauge hairline around it.
 *
 * The registry's input recesses into the page. On the mist ground the opposite
 * is right — the writing surface is the lighter one, the way a plate is lighter
 * than the wall it is bolted to.
 */
export const fieldSurface = 'border-border bg-card';

/**
 * The shell a dialog that collects a record is set in: 36rem wide, and never
 * taller than the window it opens over.
 *
 * **The width.** The registry's default is `sm:max-w-md` — 28rem — which is
 * sized for a confirmation, and it is right for one. It is too narrow for a
 * form: at that width the help text under each field wraps to three and four
 * lines and reads as a wall of prose with inputs in it. `New catalogue item` is
 * the case that made this obvious. 36rem, and deliberately not more — **the form
 * stays one column**, which is `docs/ui-conventions.md`'s rule and exists
 * because a second column fails at a till and on a narrow window, and past about
 * this width a single column stops looking composed and starts looking like a
 * label stranded from its input.
 *
 * **The height.** The registry's dialog has no ceiling at all. A form taller
 * than the window is therefore centred on it and hangs off both ends, and the
 * page behind does not scroll — so the submit button is not merely below the
 * fold, it cannot be reached. `New person` is long enough to do this on a
 * laptop.
 *
 * **Why the scroll is on the inner element and not on the popup.** The obvious
 * fix is `max-h-[85vh] overflow-y-auto` on `DialogContent`, and three dialogs
 * shipped with it. It makes the popup itself the scroll container, and the
 * registry's close button is positioned `absolute` inside that container — an
 * absolutely positioned child scrolls with the content it is in, so the ✕ leaves
 * the screen on the first scroll, exactly when a long form most needs a way out.
 * Capping the popup and scrolling a child instead leaves the ✕ where it was.
 *
 * The flex column is what makes the cap bite: a grid row sized to its content
 * overflows a container that has only a `max-height`, where a flex item with
 * `min-h-0` shrinks to it and scrolls. `p-0` and `gap-0` hand the padding and
 * the rhythm to the element that now scrolls; `cn` is tailwind-merge, so both
 * beat the registry's own `p-6 gap-6` rather than fighting them.
 *
 * For the dialogs that collect a record, not the ones that ask a question:
 * `Revoke terminal` and `Void this ticket` are two sentences and a pair of
 * buttons, and stretching them would make a confirmation look like a form to
 * fill in.
 */
export function FormDialogContent({ children }: { children: ReactNode }) {
  return (
    <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
      <div className="grid min-h-0 gap-6 overflow-y-auto p-6">{children}</div>
    </DialogContent>
  );
}
